// ============================================================================
// MessageAttachments — ЕДИНАЯ раскладка вложений сообщения (SSoT/SRP).
//
// Любое сообщение (внутренний чат и внешние каналы — без ветвлений по
// источнику) рендерит вложения ЭТИМ компонентом:
//   • аудио — единый MediaPlayer (лениво, как раньше);
//   • одно изображение — пропорциональная рамка (chatImageAttachment);
//   • несколько изображений — сетка равных плиток;
//   • видео — существующий VideoAttachment (ленивый плеер);
//   • файлы — строка с именем/размером (chatFileAttachment), md — превью.
// Ширина ограничена пузырём (см. lib/chatMessageLayout), поэтому сами
// вложения не растягивают ленту.
// ============================================================================

import { useMemo } from 'react';
import { isVoiceMessage, mediaKind } from '@/lib/fileName';
import { AudioAttachment } from '@/components/chat/chatAudioAttachment';
import { VideoAttachment } from '@/components/chat/chatVideoAttachment';
import { ChatImageAttachment, ChatImageTile, type ChatImageRef } from '@/components/chat/chatImageAttachment';
import { ChatFileAttachment, type ChatFileRef } from '@/components/chat/chatFileAttachment';

export interface ChatAttachmentRef {
  id: string;
  name: string;
  size: number;
  /** MIME от сервера (SSoT классификации: audio/webm ≠ video/webm). */
  fileType?: string;
}

interface MessageAttachmentsProps {
  attachments: ChatAttachmentRef[];
  mine: boolean;
  onDownload: (fileId: string, fileName: string) => void;
  onOpenPreview: (fileId: string, fileName: string) => void;
  autoPlayFileId?: string | null;
  onAudioEnded?: (fileId: string) => void;
  onAudioInterrupt?: () => void;
  onAudioAutoplayConsumed?: (fileId: string) => void;
}

function gridColumns(count: number): string {
  // 2/4 плитки — крупнее в две колонки; 3/5/6+ — три ровные колонки.
  return count === 2 || count === 4 ? 'grid-cols-2' : 'grid-cols-3';
}

export function MessageAttachments({
  attachments, mine, onDownload, onOpenPreview,
  autoPlayFileId, onAudioEnded, onAudioInterrupt, onAudioAutoplayConsumed,
}: MessageAttachmentsProps) {
  const groups = useMemo(() => {
    const images: ChatImageRef[] = [];
    const videos: ChatFileRef[] = [];
    const audios: ChatFileRef[] = [];
    const voices: ChatFileRef[] = [];
    const files: ChatFileRef[] = [];
    for (const a of attachments) {
      const kind = mediaKind(a.name, a.fileType);
      const ref = { id: a.id, name: a.name, size: a.size };
      if (kind === 'image') images.push(ref);
      else if (kind === 'video') videos.push(ref);
      // Голосовое (запись чата) ≠ аудиофайл (mp3/m4a/ogg): отдельный вид.
      else if (kind === 'audio') (isVoiceMessage(a.name, a.fileType) ? voices : audios).push(ref);
      else files.push(ref);
    }
    return { images, videos, audios, voices, files };
  }, [attachments]);

  return (
    <div className="mt-1.5 min-w-0 max-w-full space-y-1.5">
      {groups.voices.map((a) => (
        <AudioAttachment
          key={a.id}
          id={a.id}
          name={a.name}
          mine={mine}
          voice
          autoPlay={autoPlayFileId === a.id}
          onEnded={() => onAudioEnded?.(a.id)}
          onInterrupt={onAudioInterrupt}
          onAutoplayConsumed={() => onAudioAutoplayConsumed?.(a.id)}
          onDownload={onDownload}
        />
      ))}

      {groups.audios.map((a) => (
        <AudioAttachment
          key={a.id}
          id={a.id}
          name={a.name}
          mine={mine}
          autoPlay={autoPlayFileId === a.id}
          onEnded={() => onAudioEnded?.(a.id)}
          onInterrupt={onAudioInterrupt}
          onAutoplayConsumed={() => onAudioAutoplayConsumed?.(a.id)}
          onDownload={onDownload}
        />
      ))}

      {groups.images.length === 1 && (
        <ChatImageAttachment image={groups.images[0]} onOpen={onOpenPreview} />
      )}
      {groups.images.length > 1 && (
        <div className={`grid w-full max-w-full gap-1.5 ${gridColumns(groups.images.length)}`}>
          {groups.images.map((img) => (
            <ChatImageTile key={img.id} image={img} onOpen={onOpenPreview} />
          ))}
        </div>
      )}

      {groups.videos.map((a) => (
        <VideoAttachment key={a.id} id={a.id} name={a.name} mine={mine} />
      ))}

      {groups.files.map((a) => (
        <ChatFileAttachment
          key={a.id}
          file={a}
          mine={mine}
          onOpen={onOpenPreview}
          onDownload={onDownload}
        />
      ))}
    </div>
  );
}
